import ExcelJS from 'exceljs';
import {
  construirEmpleadoParaHistorialLaboral,
  empleadoActivoDuranteRango,
  obtenerPeriodoLaboralEnRango,
} from './fechaImportacion';

const extraerYmd = (valor) => {
  if (!valor) return null;
  const texto = String(valor);
  const coincidencia = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return coincidencia ? `${coincidencia[1]}-${coincidencia[2]}-${coincidencia[3]}` : null;
};

const codigoDocumentoAfpnet = (tipoDocumento) => {
  const tipo = String(tipoDocumento || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

  if (tipo === 'dni') return 0;
  if (tipo.includes('extranjeria')) return 1;
  if (tipo.includes('militar') || tipo.includes('policial')) return 2;
  if (tipo.includes('adolescente')) return 3;
  if (tipo.includes('pasaporte')) return 4;
  if (tipo === 'ptp' || tipo.includes('temporal de permanencia')) return 6;
  if (tipo.includes('relaciones exteriores')) return 7;
  if (tipo.includes('cedula')) return 8;
  if (tipo.includes('refugio')) return 9;
  if (tipo === 'cpp' || tipo.includes('carne permiso temporal')) return 10;
  return 0;
};

const tomarApellido = (tokens, indiceInicial) => {
  const token = String(tokens[indiceInicial] || '').toUpperCase();
  if (!token) return { valor: '', siguiente: indiceInicial };

  if (token === 'DE' && ['LA', 'LAS', 'LOS'].includes(String(tokens[indiceInicial + 1] || '').toUpperCase())) {
    return {
      valor: tokens.slice(indiceInicial, indiceInicial + 3).join(' '),
      siguiente: indiceInicial + 3,
    };
  }

  if (['DE', 'DEL', 'DA', 'DAS', 'DO', 'DOS', 'VAN', 'VON'].includes(token)) {
    return {
      valor: tokens.slice(indiceInicial, indiceInicial + 2).join(' '),
      siguiente: indiceInicial + 2,
    };
  }

  return { valor: tokens[indiceInicial], siguiente: indiceInicial + 1 };
};

/**
 * Los empleados del sistema se registran como APELLIDO PATERNO + APELLIDO MATERNO + NOMBRES.
 * También reconoce partículas frecuentes (DE LA, DEL, VAN, VON).
 */
export const separarNombreAfpnet = (nombreCompleto) => {
  const tokens = String(nombreCompleto || '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { paterno: '', materno: '', nombres: '' };
  if (tokens.length === 1) return { paterno: tokens[0], materno: '', nombres: '' };

  const paterno = tomarApellido(tokens, 0);
  const materno = tomarApellido(tokens, paterno.siguiente);
  return {
    paterno: paterno.valor,
    materno: materno.valor,
    nombres: tokens.slice(materno.siguiente).join(' '),
  };
};

const codigoConcepto = (concepto) =>
  String(concepto?.codigoPlame || concepto?.codigo_plame || concepto?.codigo || '').padStart(4, '0');

export const obtenerBaseImponibleAfp = (empleado) => {
  const descuentos = Array.isArray(empleado?.conceptos?.descuentos)
    ? empleado.conceptos.descuentos
    : [];
  const aportes = Array.isArray(empleado?.conceptos?.aportes) ? empleado.conceptos.aportes : [];
  const conceptosAfp = [...descuentos, ...aportes].filter((concepto) =>
    ['0608', '0601', '0606', '0611', '0809', '0616'].includes(codigoConcepto(concepto))
  );

  const conceptoConBase = conceptosAfp.find(
    (concepto) => Number(concepto.base_calculo ?? concepto.base_imponible) > 0
  );
  if (conceptoConBase) {
    return Math.round(Number(conceptoConBase.base_calculo ?? conceptoConBase.base_imponible) * 100) / 100;
  }

  // Compatibilidad con planillas cerradas antiguas que guardaron base_imponible = 0:
  // el aporte obligatorio 0608 es 10% de la misma remuneración asegurable.
  const aporteObligatorio = descuentos.find((concepto) => codigoConcepto(concepto) === '0608');
  const tasaAporte = Number(empleado?.afp_aporte) || 10;
  const montoAporte = Number(aporteObligatorio?.monto ?? aporteObligatorio?.valor) || 0;
  if (montoAporte > 0 && tasaAporte > 0) {
    return Math.round((montoAporte / (tasaAporte / 100)) * 100) / 100;
  }

  return 0;
};

const esRegimenMinero = (empleado) => {
  const codigo = String(empleado?.regimen_laboral_codigo || '').toUpperCase();
  const nombre = String(empleado?.regimen_laboral_nombre || empleado?.regimenLaboral || '').toLowerCase();
  return ['MINGEN', 'MINMIC', 'MINMYP'].includes(codigo) || nombre.includes('minero');
};

const estaDentroDelPeriodo = (fecha, inicio, fin) =>
  Boolean(fecha && inicio && fin && fecha >= inicio && fecha <= fin);

/**
 * AFPnet:
 * - Relación laboral = S si el trabajador estuvo vigente en el mes de la planilla.
 * - Inicio RL = S solo si su fecha de ingreso real cae dentro del mes.
 * - Cese RL = S solo si su fecha de cese real cae dentro del mes.
 *
 * Empleado típico activo todo el mes → S, N, N.
 */
const obtenerIndicadoresRelacionLaboral = (empleado, fechaInicio, fechaFin) => {
  const empleadoHistorial = construirEmpleadoParaHistorialLaboral(empleado);
  const periodo = obtenerPeriodoLaboralEnRango(empleadoHistorial, fechaInicio, fechaFin);

  const ingresoReal = extraerYmd(
    periodo?.fecha_ingreso ||
      empleado.fecha_ingreso_actual ||
      empleado.fechaIngreso ||
      empleado.fecha_ingreso
  );

  // Si hay periodo del historial, solo usar su cese (null = vigente). No mezclar con cese antiguo.
  const ceseReal = periodo
    ? extraerYmd(periodo.fecha_cese)
    : extraerYmd(
        empleado.fecha_cese_actual || empleado.fechaCese || empleado.fecha_cese
      );

  let relacionVigente = empleadoActivoDuranteRango(empleadoHistorial, fechaInicio, fechaFin);
  if (!relacionVigente && ingresoReal) {
    relacionVigente = ingresoReal <= fechaFin && (!ceseReal || ceseReal >= fechaInicio);
  }
  // Quien está en la planilla con base AFP imponible estuvo vigente en el mes
  if (!relacionVigente && obtenerBaseImponibleAfp(empleado) > 0) {
    relacionVigente = true;
  }

  return {
    relacionLaboral: relacionVigente ? 'S' : 'N',
    inicioRl: estaDentroDelPeriodo(ingresoReal, fechaInicio, fechaFin) ? 'S' : 'N',
    ceseRl: estaDentroDelPeriodo(ceseReal, fechaInicio, fechaFin) ? 'S' : 'N',
  };
};

const construirFila = (empleado, indice, fechaInicio, fechaFin) => {
  const nombre = separarNombreAfpnet(empleado.nombre_completo || empleado.nombre);
  const indicadores = obtenerIndicadoresRelacionLaboral(empleado, fechaInicio, fechaFin);

  return [
    indice + 1,
    String(empleado.cuspp || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase(),
    codigoDocumentoAfpnet(empleado.tipo_documento),
    String(empleado.numero_documento || empleado.documento || ''),
    nombre.paterno,
    nombre.materno,
    nombre.nombres,
    indicadores.relacionLaboral,
    indicadores.inicioRl,
    indicadores.ceseRl,
    '',
    obtenerBaseImponibleAfp(empleado),
    0,
    0,
    0,
    esRegimenMinero(empleado) ? 'M' : 'N',
    '',
  ];
};

export const generarExcelAfpnet = async ({ empleados, planilla, empresa }) => {
  const fechaInicio = extraerYmd(planilla?.fecha_inicio);
  const fechaFin = extraerYmd(planilla?.fecha_fin);
  if (!fechaInicio || !fechaFin) {
    throw new Error('La planilla no tiene un período válido para AFPnet');
  }

  const afiliados = (empleados || []).filter((empleado) => {
    const sistema = String(empleado.sistema_pensionario || empleado.tipoAporte || '').toUpperCase();
    return sistema === 'AFP' || Boolean(empleado.cuspp);
  });
  if (afiliados.length === 0) {
    throw new Error('La planilla cerrada no tiene trabajadores afiliados a una AFP');
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sistema de Planillas';
  workbook.created = new Date();
  const worksheet = workbook.addWorksheet('AFPnet');

  afiliados.forEach((empleado, indice) => {
    worksheet.addRow(construirFila(empleado, indice, fechaInicio, fechaFin));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  const periodo = fechaInicio.slice(0, 7).replace('-', '');
  const ruc = String(empresa?.ruc || '').replace(/\D/g, '');
  link.href = url;
  link.download = `AFPnet_${ruc || 'empresa'}_${periodo}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return {
    total: afiliados.length,
    normales: afiliados.filter((empleado) => !esRegimenMinero(empleado)).length,
    mineros: afiliados.filter(esRegimenMinero).length,
    sinBase: afiliados.filter((empleado) => obtenerBaseImponibleAfp(empleado) <= 0).length,
  };
};
